import { objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { createContext, useMemo, useEffect, useContext } from 'react';
import { useCallbackRef, utils } from '@costar/theme-ui';
import { createCurrentLayoutSelector } from './selectors/current-layout-selector.esm.js';
import { useOrchestrator } from './orchestrator.esm.js';
import { createConnectedModifyTableLayoutComponent } from '../connected-modify-table-layout/connected-modify-table-layout.esm.js';
import { bootstrapServices } from '../../services/bootstrap-services.esm.js';
import { LayoutType } from '../../types/layouts.esm.js';

function createConnectedModifyTableLayout() {
  var context = /*#__PURE__*/createContext(undefined);
  var Provider = context.Provider;
  var currentLayoutSelector = createCurrentLayoutSelector();
  var currentLayoutContext = /*#__PURE__*/createContext(undefined);
  var CurrentLayoutProvider = currentLayoutContext.Provider;
  var ModifyTableLayoutProvider = function ModifyTableLayoutProvider(props) {
    var allFields = props.allFields,
      categories = props.categories,
      resources = props.resources,
      onLayoutsChanged = props.onLayoutsChanged,
      delUserEntity = props.delUserEntity,
      setUserEntity = props.setUserEntity,
      getUserEntities = props.getUserEntities,
      entityTag = props.entityTag,
      groupTag = props.groupTag,
      fontSize = props.fontSize;
    var onLayoutsChangedProp = useCallbackRef(onLayoutsChanged);
    var services = useMemo(function () {
      return bootstrapServices({
        delUserEntity: delUserEntity,
        setUserEntity: setUserEntity,
        getUserEntities: getUserEntities,
        entityTag: entityTag,
        groupTag: groupTag
      });
    }, [delUserEntity, entityTag, getUserEntities, groupTag, setUserEntity]);
    var showModifyTableLayout = !!services;
    var _useOrchestrator = useOrchestrator(_objectSpread2(_objectSpread2({}, props), {}, {
        loadLayouts: services === null || services === void 0 ? void 0 : services.loadLayouts
      })),
      layouts = _useOrchestrator.layouts,
      setLayouts = _useOrchestrator.setLayouts;
    useEffect(function () {
      if (layouts && showModifyTableLayout) {
        onLayoutsChangedProp(layouts);
      }
    }, [layouts, onLayoutsChangedProp, showModifyTableLayout]);
    var value = useMemo(function () {
      return {
        layouts: layouts,
        setLayouts: setLayouts,
        services: services,
        allFields: allFields,
        categories: categories,
        resources: resources,
        fontSize: fontSize
      };
    }, [allFields, categories, layouts, services, setLayouts, resources, fontSize]);
    var currentLayout = currentLayoutSelector(layouts);
    //SSR compatibility
    if (!utils.isBrowser) {
      var layout = (currentLayout === null || currentLayout === void 0 ? void 0 : currentLayout.currentLayout) || (Array.isArray(layouts) ? layouts[0] : {
        type: LayoutType.Default
      });
      return /*#__PURE__*/React.createElement(Provider, {
        value: value
      }, /*#__PURE__*/React.createElement(CurrentLayoutProvider, {
        value: currentLayout
      }, typeof props.children === 'function' ? props.children(layout) : props.children));
    }
    if (!currentLayout) {
      if (props.loading) {
        return props.loading();
      } else {
        return null;
      }
    }
    return /*#__PURE__*/React.createElement(Provider, {
      value: value
    }, /*#__PURE__*/React.createElement(CurrentLayoutProvider, {
      value: currentLayout
    }, typeof props.children === 'function' ? props.children(currentLayout.currentLayout) : props.children));
  };
  var useCurrentLayout = function useCurrentLayout() {
    var value = useContext(currentLayoutContext);
    if (value === undefined) {
      throw new Error('The provider ModifyTableLayoutProvider needs to be added to the component tree');
    }
    return value;
  };
  var useModifyTableLayoutContext = function useModifyTableLayoutContext() {
    var value = useContext(context);
    if (value === undefined) {
      throw new Error('The provider ModifyTableLayoutProvider needs to be added to the component tree');
    }
    return value;
  };
  var ConnectedModifyTableLayout = createConnectedModifyTableLayoutComponent(useModifyTableLayoutContext);
  return {
    ModifyTableLayoutProvider: ModifyTableLayoutProvider,
    ConnectedModifyTableLayout: ConnectedModifyTableLayout,
    useCurrentLayout: useCurrentLayout
  };
}

export { createConnectedModifyTableLayout };
//# sourceMappingURL=modify-table-layout-provider.esm.js.map
